import React from "react";
import { Calendar } from "lucide-react";
import { TIME_PRESETS, TIME_PRESET_SHORT } from "../../lib/timeFilter";

/**
 * TimeFilterBar — Iter80 (shared).
 *
 * Compact, controlled time-range picker reused by:
 *   - Rapports (ExecutiveReportsTab)
 *   - Tableau (CommandCenterTab)
 *   - Tableau · Résumé analytique
 *   - Alertes (AdminAlertsTab)
 *
 * Design rules:
 *   - CONTROLLED — parent owns `preset|customSince|customUntil` and
 *     receives `onChange({preset, customSince, customUntil})` on every
 *     interaction. Never keeps its own state.
 *   - Layout: horizontal chip row on desktop, wraps naturally on
 *     narrower widths; custom date/time inputs collapse below when the
 *     `custom` preset is active (per user spec §6).
 *   - No side effects, no data fetching. The consumer decides whether
 *     to refetch on every change or wait for an "Appliquer" click.
 */
export const TimeFilterBar = ({
  preset,
  customSince,
  customUntil,
  onChange,
  short = false,              // when true, use "1 h" / "24 h" labels instead of "Dernière heure"
  testidPrefix = "tf",
  className = "",
}) => {
  const emit = (patch) =>
    onChange?.({
      preset,
      customSince,
      customUntil,
      ...patch,
    });

  return (
    <div
      className={`space-y-2 ${className}`}
      data-testid={`${testidPrefix}-bar`}
    >
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testidPrefix}-preset-row`}>
        <Calendar className="w-3.5 h-3.5 text-zinc-500 shrink-0" aria-hidden />
        {TIME_PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => emit({ preset: p.id })}
              data-testid={`${testidPrefix}-preset-${p.id}`}
              aria-pressed={active}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                active
                  ? "bg-emerald-500 text-black border-emerald-500"
                  : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
              }`}
            >
              {short ? TIME_PRESET_SHORT[p.id] || p.label : p.label}
            </button>
          );
        })}
      </div>

      {preset === "custom" && (
        <div
          className="flex items-center gap-1.5 flex-wrap"
          data-testid={`${testidPrefix}-custom-row`}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            Du
          </span>
          <input
            type="datetime-local"
            value={customSince || ""}
            onChange={(e) => emit({ customSince: e.target.value })}
            data-testid={`${testidPrefix}-custom-since`}
            aria-label="Date et heure de début"
            className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-2 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            Au
          </span>
          <input
            type="datetime-local"
            value={customUntil || ""}
            onChange={(e) => emit({ customUntil: e.target.value })}
            data-testid={`${testidPrefix}-custom-until`}
            aria-label="Date et heure de fin"
            className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-2 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
          />
        </div>
      )}
    </div>
  );
};

export default TimeFilterBar;
